import { useQuery, UseQueryResult } from 'react-query';
import { Article } from '../../models';
import { isPdfContentComponent } from '../../services/articles/articles.service';
import { ArticleDto } from '../../services/dtos/article/article.dto';
import { ArticleComponentDto } from '../../services/dtos/article/articleComponent.dto';
import { ApiError } from '../../services/api-error.model';
import { getArticleIdFromHref, getCompanyIdFromHref } from '../../utils/articleUtils';
import { modifyHtml } from '../../utils/htmlUtils';
import { queryKeys } from '../queryKeyFactory';
import { mapArticleDtoToArticle } from './utils/article.mappers';

const modifyBody = (body: string, articleDto: ArticleDto) =>
  modifyHtml(body, (html) => {
    html.querySelectorAll('a').forEach((anchorTag) => {
      anchorTag.classList.add('content-link');
      if (!anchorTag.hash) {
        if (!getCompanyIdFromHref(anchorTag.href)) {
          // eslint-disable-next-line no-param-reassign
          anchorTag.target = '_blank';
        }
      } else {
        const hrefWithoutHash = anchorTag.href.replace(anchorTag.hash, '');
        const articleId = getArticleIdFromHref(hrefWithoutHash);
        if (articleId !== articleDto.id) {
          // eslint-disable-next-line no-param-reassign
          anchorTag.target = '_blank';
        }
      }
    });
    html.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.removeAttribute('loading');
    });
  });

const transformContentComponents = (articleDto: ArticleDto): Promise<ArticleComponentDto[]> => {
  return Promise.all(
    articleDto.content.components.map(async (component) => {
      const isPdfContent = await isPdfContentComponent(component);
      if (isPdfContent) {
        return {
          ...component,
          file: {
            url: component.file!.url,
            pdfContent: true,
          },
        };
      }

      if (component.body) {
        const modifiedBody = modifyBody(component.body, articleDto);
        return { ...component, body: modifiedBody };
      }

      return component;
    })
  );
};

export default (article: ArticleDto | undefined): UseQueryResult<Article | undefined, ApiError> => {
  return useQuery<Article | undefined, Error>(queryKeys.fetchArticles.content(article || null), async () => {
    if (!article) {
      return undefined;
    }

    const articleDto = {
      ...article,
      content: {
        ...article.content,
        components: await transformContentComponents(article),
      },
    };

    return mapArticleDtoToArticle(articleDto);
  });
};
